import { Spin } from "antd";
import i18n from "i18next";
import type { FC } from "react";
import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router";

import type { AidPreferenceQueryParams } from "src/app/features/EmvKernelProfile/subFeatures/AidPreference/services/server/AidPreferenceQueryTypes";
import { FormManager } from "../../../../../../../components/Form/FormManager";
import { useFormSubmitCompletion } from "../../../../../../../components/Form/hooks/useFormSubmitCompletion";
import { useLoadFormDefinitionQuery } from "../../../../../../../components/Form/services/server/serverApi";
import { FormLayoutType } from "../../../../../../../components/Form/types";
import type { FormProps } from "../../../../../../../components/Form/types/FormProps";
import { ResourceName, ResourceVariant } from "../../../../../../../core/router/config/ResourceName";
import { BASE_PATH, FORM_ACTION } from "../../../../../../../core/router/types/routerTypes";
import { useAidPreferenceSaveMutation } from "../../services/server/AidPreferenceApi";
import type { AidPreferenceEntity } from "../../types/AidPreferenceEntity";

export const AidPreferenceCreateForm: FC<FormProps> = ({ runSubmit, formAction }) => {
    const { data: formDefinition, isFetching: configIsFetching } = useLoadFormDefinitionQuery("aid-preference");
    const { emvKernelProfileId } = useParams();
    const [queryConfig, setQueryConfig] = useState<AidPreferenceQueryParams>({});

    // set save params
    useEffect(() => {
        setQueryConfig({ emvKernelProfileId });
    }, [emvKernelProfileId]);

    const [formData, setFormData] = useState<AidPreferenceEntity>({} as AidPreferenceEntity);
    const onChangeCallback = useCallback(setFormData, [setFormData]);

    const backUrl = `${BASE_PATH}/${ResourceName.EMV_KERNEL_PROFILES}/${ResourceVariant.APAC}/${FORM_ACTION.EDIT}/${emvKernelProfileId}`;
    const completeSubmit = useFormSubmitCompletion(formAction, backUrl);
    const [saveFormData, { isLoading: saveFormIsInProgress }] = useAidPreferenceSaveMutation();
    const onValidatedCallback = useCallback(
        async (valid: boolean) => {
            if (!valid || !formData) {
                return;
            }

            const result = await saveFormData({ aidPreferenceFormData: formData, aidPreferenceQuery: queryConfig });

            const hasError = "error" in result;
            if (!hasError) {
                completeSubmit(
                    i18n.t("common.formSaveSuccess", {
                        id: result.data.id,
                        resource: "AidPreference",
                    }),
                    backUrl,
                );
            }
        },
        [formData, saveFormData, queryConfig, completeSubmit, backUrl],
    );

    return (
        <Spin spinning={saveFormIsInProgress || configIsFetching}>
            {formDefinition?.formConfiguration && (
                <FormManager<AidPreferenceEntity>
                    name="AidPreferenceCreateForm"
                    formData={formData}
                    formLayout={FormLayoutType.TABS}
                    formDefinition={formDefinition.formConfiguration}
                    onChange={onChangeCallback}
                    shouldValidate={runSubmit}
                    onValidated={onValidatedCallback}
                />
            )}
        </Spin>
    );
};
